import React, { useCallback, useState } from "react";
import { GoogleMap, MarkerF, InfoWindowF } from "@react-google-maps/api";
import { Modal, Box } from "@mui/material";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import closeIcon from "../../assets/images/closeicon.svg";
import {
  fetchOnSelectionData,
  markerAction,
} from "../../Actions/BuildingManagementAction";
import "./MapModal.scss";

const center = {
  lat: 39.8283,
  lng: -98.5795,
};

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "80%",
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 1,
  p: 3,
};

const MapModal = ({
  open,
  onClose,
  markerData,
  loadError,
  isLoaded,
  type,
  customClass,
  markerList,
  onMarkerClick,
}) => {
  const dispatch = useDispatch();
  const { lat, lng } = markerData || {};

  const [hoveredMarkerId, setHoveredMarkerId] = useState(null);
  const parsedLat = parseFloat(lat);
  const parsedLng = parseFloat(lng);
  const isValid = !isNaN(parsedLat) && !isNaN(parsedLng);

  const handleClick = useCallback(
    (e) => {
      const clickedLat = e.latLng.lat();
      const clickedLng = e.latLng.lng();
      dispatch(markerAction({ lat: clickedLat, lng: clickedLng }));

      const latlng = { lat: clickedLat, lng: clickedLng };
      const geocoder = new window.google.maps.Geocoder();

      geocoder.geocode({ location: latlng }, (results, status) => {
        if (status === "OK" && results[0]) {
          const placeId = results[0].place_id;

          if (placeId) {
            const placeService = new window.google.maps.places.PlacesService(
              document.createElement("div")
            );

            placeService.getDetails({ placeId }, (place, placeStatus) => {
              if (
                placeStatus ===
                  window.google.maps.places.PlacesServiceStatus.OK &&
                place
              ) {
                const rawAddress =
                  place.formatted_address || results[0].formatted_address;
                // NOSONAR
                const cleanedAddress = rawAddress.replace(/^[\w+]+\s+/g, "");
                const locationData = {
                  lat: clickedLat,
                  lng: clickedLng,
                  name: place.name || cleanedAddress,
                  address: cleanedAddress,
                  placeId: place.place_id,
                  types: place.types,
                  url: place.url || null,
                  website: place.website,
                  phoneNumber: place.formatted_phone_number,
                  fullDetails: place,
                };

                dispatch(fetchOnSelectionData(locationData));
              } else {
                fallbackToGeocoderResult();
              }
            });
          } else {
            fallbackToGeocoderResult();
          }

          function fallbackToGeocoderResult() {
            const rawFallbackAddress = results[0].formatted_address;
            // NOSONAR
            const cleanedFallbackAddress = rawFallbackAddress.replace(
              /^[\w+]+\s+/g,
              ""
            );
            dispatch(
              fetchOnSelectionData({
                lat: clickedLat,
                lng: clickedLng,
                name: cleanedFallbackAddress,
                address: cleanedFallbackAddress,
                placeId: null,
                url: null,
              })
            );
          }
        } else {
          dispatch(fetchOnSelectionData({ lat: clickedLat, lng: clickedLng }));
        }
      });
    },
    [dispatch]
  );

  const renderMarker = () => {
    if (!isValid) return null;
    return <MarkerF onClick={() => {

    }} position={{ lat: parsedLat, lng: parsedLng }} />;
  };

 const renderAllMarkers = () => {
  if (!Array.isArray(markerList)) return null;
  const google = window.google;

  return markerList
    .filter(
      (item) =>
        item.latitude !== null &&
        item.longitude !== null &&
        !isNaN(parseFloat(item.latitude)) &&
        !isNaN(parseFloat(item.longitude))
    )
    .map((item) => {
      const isLockdown = item.lockdown === true;
      const criticalHealth = !item.healthStatus;

      let iconUrl;
      let tooltipIcon;
      if (type === "screen") {
        if (isLockdown && item.drillMode) {
          iconUrl = "/drill.png";
          tooltipIcon = "/tooltip-drill.png";
        } else if (isLockdown && item.testMode) {
          iconUrl = "/test.png";
          tooltipIcon = "/tooltip-test.png";
        } else if(criticalHealth) {
          iconUrl = isLockdown ? "/lockdown-marker.png" : "/map-marker-critical.svg";
           tooltipIcon = isLockdown ? "/tooltip-deactivate.png" : "/map-marker-critical.svg";
        } else {
          iconUrl = isLockdown ? "/lockdown-marker.png" : "/custom-marker.png";
          tooltipIcon = isLockdown
            ? "/tooltip-deactivate.png"
            : "/tooltip-activate.png";
        }
      } else {
        iconUrl = "http://maps.google.com/mapfiles/ms/icons/red-dot.png";
      }

      const position = {
        lat: parseFloat(item.latitude),
        lng: parseFloat(item.longitude),
      };

      return (
        <React.Fragment key={item.id}>
          <MarkerF
            position={position}
            onMouseOver={() => setHoveredMarkerId(item.id)}
            onMouseOut={() => setHoveredMarkerId(null)}
            aria-label={`Map marker for ${item.customerName || "building"}`}
            onClick={() => {
              if (onMarkerClick) onMarkerClick(hoveredMarkerId);
            }}
            icon={
              google
                ? {
                    url: iconUrl,
                    scaledSize: new google.maps.Size(32, 40),
                    origin: new google.maps.Point(0, 0),
                    anchor: new google.maps.Point(20, 49),
                  }
                : undefined
            }
          />
          {hoveredMarkerId === item.id && google && (
            <InfoWindowF
              position={position}
              options={{
                pixelOffset: new google.maps.Size(0, -50),
                disableAutoPan: true,
                closeBoxURL: "",
              }}
            >
              <div className="tooltipText">
                <img
                  src={tooltipIcon}
                  alt="Marker Icon"
                  style={{ width: 28, height: 28, marginBottom: 8 }}
                />
                <strong>{item.customerName}</strong>
                <span>{item.address}</span>
              </div>
            </InfoWindowF>
          )}
        </React.Fragment>
      );
    });
};

  if (loadError) return <div>Error loading maps</div>;
  if (!isLoaded) return <div>Loading maps...</div>;

  return type === "modal" ? (
    <Modal open={open} onClose={onClose}  className="addFlorModal">
      <Box sx={modalStyle}>
        <div className="Modal_header">
          <h3 data-testid="Select-Location">Select Location</h3>
          <button onClick={onClose} className="close_button">
              <img src={closeIcon} alt="" />
          </button>
        </div>
        <div
  role="presentation"
  aria-label={
    type === "modal"
      ? "Interactive map to select building location. Click on a location to select it."
      : "Interactive map displaying building markers and their current status."
  }
>
        <GoogleMap
          mapContainerClassName={customClass}
          center={center}
          zoom={4}
          onClick={handleClick}
          data-testid="google-map"
            options={{
            fullscreenControl: false,
           mapTypeControl: false,
            streetViewControl: false,
            zoomControl: true,
          }}
        >
          {renderMarker()}
        </GoogleMap>
</div>
      </Box>
    </Modal>
  ) : (
    <Box>
      <div
      role="presentation"
      aria-label={
        type === "modal"
          ? "Interactive map to select building location. Click on a location to select it."
          : "Interactive map displaying building markers and their current status."
      }
    > 
        <GoogleMap
          mapContainerClassName={customClass}
          center={center}
          zoom={4}
          data-testid="google-map"
           onLoad={(map) => {
            const controls = document.querySelector('.gm-fullscreen-control');
            if (controls) controls.style.display = 'none';
          }}
          onClick={handleClick}
            options={{
            fullscreenControl: false,
            mapTypeControl: false,
            streetViewControl: false,
            zoomControl: true,
          }}
        >
          {renderAllMarkers()}
        </GoogleMap>
    </div>  
    </Box>
  );
};

MapModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  markerData: PropTypes.shape({
    lat: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    lng: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  loadError: PropTypes.any,
  isLoaded: PropTypes.bool.isRequired,
  type: PropTypes.string.isRequired,
  customClass: PropTypes.string.isRequired,
  markerList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      latitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      longitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      address: PropTypes.string,
      lockdown: PropTypes.bool,
    })
  ),
  onMarkerClick: PropTypes.func,
};

export default MapModal;
